import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, Edit3, Save, X, Plus, Trash2, Calendar, User,
  Paperclip, Sparkles, AlertCircle, CheckCircle2,
  Clock, XCircle, Building2, Truck, FileText, Hammer, Type, Loader2, ExternalLink,
  Upload, Globe, Download, Search, Zap, ClipboardList, ChevronDown, ChevronUp
} from 'lucide-react'
import api from '../../../core/services/api'
import { Toast } from '../../../components/Toast'
import ConfirmModal from '../../../components/ConfirmModal'
import HorizontalTimeline from '../../../components/fascicoli/HorizontalTimeline'
import LinkSorvegliatiPanel from '../../../components/fascicoli/LinkSorvegliatiPanel'
import SchedePerEntita from '../../../components/schede/SchedePerEntita'
import ClienteStatsCard from '../../../components/ClienteStatsCard'
import type { FascicoloDetail, AzioneFascicolo, StatoFascicolo, StatoAzione, PersonaleMinimal } from '../../../types/fascicoli'

const statoBadge: Record<StatoFascicolo, { label: string; bg: string; text: string }> = {
  aperto: { label: 'Aperto', bg: 'bg-blue-100', text: 'text-blue-700' },
  chiuso: { label: 'Chiuso', bg: 'bg-green-100', text: 'text-green-700' },
  archiviato: { label: 'Archiviato', bg: 'bg-gray-100', text: 'text-gray-700' },
}

const statoAzioneBadge: Record<StatoAzione, { label: string; bg: string; text: string; icon: React.ElementType }> = {
  pianificata: { label: 'Pianificata', bg: 'bg-blue-100', text: 'text-blue-700', icon: Clock },
  completata: { label: 'Completata', bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle2 },
  annullata: { label: 'Annullata', bg: 'bg-gray-100', text: 'text-gray-500', icon: XCircle },
}

const tipoIcon: Record<string, React.ElementType> = {
  cliente: Building2,
  fornitore: Truck,
  offerta: FileText,
  commessa: Hammer,
  testo_libero: Type,
}

export default function FascicoloDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [fascicolo, setFascicolo] = useState<FascicoloDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [editMode, setEditMode] = useState(false)
  const [editForm, setEditForm] = useState({ titolo: '', descrizione: '', note: '', stato: '' as StatoFascicolo | '' })

  // Nuova azione
  const [showNewAzione, setShowNewAzione] = useState(false)
  const [azioneForm, setAzioneForm] = useState({
    data_azione: new Date().toISOString().split('T')[0],
    titolo: '',
    descrizione: '',
    esito: '',
    responsabile_id: '',
    stato: 'pianificata' as StatoAzione,
    is_scadenza: false,
    data_scadenza: '',
    giorni_preavviso: 7,
    crea_evento_calendario: false,
    data_evento: '',
  })
  const [creatingAzione, setCreatingAzione] = useState(false)

  // Edit azione
  const [editingAzioneId, setEditingAzioneId] = useState<string | null>(null)
  const [editAzioneForm, setEditAzioneForm] = useState<any>(null)

  // (AI gestita tramite fonti)

  // Personale
  const [personale, setPersonale] = useState<PersonaleMinimal[]>([])

  // Conferma eliminazione
  const [showDeleteFascicolo, setShowDeleteFascicolo] = useState(false)
  const [deleteAzioneId, setDeleteAzioneId] = useState<string | null>(null)

  // Ricerca e card espanse
  const [searchFilter, setSearchFilter] = useState('')
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const azioneRefs = useRef<Record<string, HTMLDivElement | null>>({})

  // Conferma eliminazione fonte
  const [confirmDeleteFonte, setConfirmDeleteFonte] = useState<{ azioneId: string; indice: number; titolo: string } | null>(null)

  // Modale Fonti Esterne
  const [fonteModalAzioneId, setFonteModalAzioneId] = useState<string | null>(null)
  const [fonteModalTipo, setFonteModalTipo] = useState<'link' | 'allegato'>('link')
  const [fonteModalUrl, setFonteModalUrl] = useState('')
  const [fonteModalTitolo, setFonteModalTitolo] = useState('')
  const [fonteModalLoading, setFonteModalLoading] = useState(false)


  useEffect(() => {
    if (id) {
      loadFascicolo()
      loadPersonale()
    }
  }, [id])

  const loadFascicolo = async () => {
    try {
      setLoading(true)
      const res = await api.get(`/v1/fascicoli/${id}`)
      setFascicolo(res.data)
      setEditForm({
        titolo: res.data.titolo,
        descrizione: res.data.descrizione || '',
        note: res.data.note || '',
        stato: res.data.stato,
      })
    } catch {
      setToast({ message: 'Errore caricamento fascicolo', type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const loadPersonale = async () => {
    try {
      const res = await api.get('/v1/personale')
      setPersonale(res.data.filter((p: any) => p.stato === 'attivo'))
    } catch { /* ignore */ }
  }

  const handleSaveEdit = async () => {
    try {
      await api.put(`/v1/fascicoli/${id}`, {
        titolo: editForm.titolo,
        descrizione: editForm.descrizione || null,
        note: editForm.note || null,
        stato: editForm.stato,
      })
      setEditMode(false)
      loadFascicolo()
      setToast({ message: 'Fascicolo aggiornato', type: 'success' })
    } catch {
      setToast({ message: 'Errore aggiornamento', type: 'error' })
    }
  }

  const confirmDeleteFascicolo = async () => {
    try {
      await api.delete(`/v1/fascicoli/${id}`)
      navigate('/app/fascicoli')
      setToast({ message: 'Fascicolo eliminato', type: 'success' })
    } catch {
      setToast({ message: 'Errore eliminazione', type: 'error' })
    } finally {
      setShowDeleteFascicolo(false)
    }
  }

  // ---- Azioni ----


  const handleCreateAzione = async () => {
    if (!azioneForm.titolo || !azioneForm.responsabile_id) {
      setToast({ message: 'Compila titolo e responsabile', type: 'error' })
      return
    }
    setCreatingAzione(true)
    try {
      await api.post(`/v1/fascicoli/${id}/azioni`, {
        ...azioneForm,
        data_scadenza: azioneForm.is_scadenza ? azioneForm.data_scadenza : undefined,
        crea_evento_calendario: azioneForm.crea_evento_calendario || undefined,
        data_evento: azioneForm.crea_evento_calendario && azioneForm.data_evento ? azioneForm.data_evento : undefined,
      })
      setShowNewAzione(false)
      setAzioneForm({
        data_azione: new Date().toISOString().split('T')[0],
        titolo: '', descrizione: '', esito: '', responsabile_id: '',
        stato: 'pianificata', is_scadenza: false, data_scadenza: '', giorni_preavviso: 7,
        crea_evento_calendario: false, data_evento: '',
      })
      setToast({ message: 'Azione creata', type: 'success' })
      loadFascicolo()
    } catch {
      setToast({ message: 'Errore creazione azione', type: 'error' })
    } finally {
      setCreatingAzione(false)
    }
  }

  const startEditAzione = (azione: AzioneFascicolo) => {
    setEditingAzioneId(azione.id)
    setEditAzioneForm({
      data_azione: azione.data_azione,
      titolo: azione.titolo,
      descrizione: azione.descrizione || '',
      esito: azione.esito || '',
      responsabile_id: azione.responsabile_id,
      stato: azione.stato,
      is_scadenza: azione.is_scadenza,
      data_scadenza: azione.data_scadenza || '',
      giorni_preavviso: azione.giorni_preavviso,
      links: azione.links || [],
    })
  }

  const handleSaveAzione = async (azioneId: string) => {
    try {
      await api.put(`/v1/fascicoli/${id}/azioni/${azioneId}`, {
        ...editAzioneForm,
        data_scadenza: editAzioneForm.is_scadenza ? editAzioneForm.data_scadenza : null,
      })
      setEditingAzioneId(null)
      setEditAzioneForm(null)
      setToast({ message: 'Azione aggiornata', type: 'success' })
      loadFascicolo()
    } catch {
      setToast({ message: 'Errore aggiornamento azione', type: 'error' })
    }
  }

  const confirmDeleteAzione = async () => {
    if (!deleteAzioneId) return
    try {
      await api.delete(`/v1/fascicoli/${id}/azioni/${deleteAzioneId}`)
      loadFascicolo()
      setToast({ message: 'Azione eliminata', type: 'success' })
    } catch {
      setToast({ message: 'Errore eliminazione azione', type: 'error' })
    } finally {
      setDeleteAzioneId(null)
    }
  }

  // ---- Fonti Esterne ----

  const openFonteModal = (azioneId: string) => {
    setFonteModalAzioneId(azioneId)
    setFonteModalTipo('link')
    setFonteModalUrl('')
    setFonteModalTitolo('')
  }

  const closeFonteModal = () => {
    setFonteModalAzioneId(null)
    setFonteModalUrl('')
    setFonteModalTitolo('')
  }

  const handleAddFonteLink = async () => {
    if (!fonteModalAzioneId || !fonteModalUrl) return
    setFonteModalLoading(true)
    try {
      const formData = new FormData()
      formData.append('tipo', 'link')
      formData.append('url', fonteModalUrl)
      formData.append('titolo', fonteModalTitolo || fonteModalUrl)
      await api.post(`/v1/fascicoli/${id}/azioni/${fonteModalAzioneId}/fonti`, formData)
      setToast({ message: 'Link aggiunto e analizzato', type: 'success' })
      closeFonteModal()
      loadFascicolo()
    } catch (err: any) {
      const detail = err?.response?.data?.detail
      const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map((d: any) => d.msg).join(', ') : 'Errore aggiunta link'
      setToast({ message: msg, type: 'error' })
    } finally {
      setFonteModalLoading(false)
    }
  }

  const handleAddFonteAllegato = () => {
    if (!fonteModalAzioneId) return
    const input = document.createElement('input')
    input.type = 'file'
    input.onchange = async (e: any) => {
      const file = e.target.files?.[0]
      if (!file) return
      setFonteModalLoading(true)
      try {
        const formData = new FormData()
        formData.append('tipo', 'allegato')
        formData.append('file', file)
        formData.append('titolo', fonteModalTitolo || file.name)
        await api.post(`/v1/fascicoli/${id}/azioni/${fonteModalAzioneId}/fonti`, formData)
        setToast({ message: 'Allegato caricato e analizzato', type: 'success' })
        closeFonteModal()
        loadFascicolo()
      } catch (err: any) {
        const detail = err?.response?.data?.detail
        const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map((d: any) => d.msg).join(', ') : 'Errore caricamento'
        setToast({ message: msg, type: 'error' })
      } finally {
        setFonteModalLoading(false)
      }
    }
    input.click()
  }

  const handleRemoveFonte = async (azioneId: string, indice: number) => {
    try {
      await api.delete(`/v1/fascicoli/${id}/azioni/${azioneId}/fonti/${indice}`)
      loadFascicolo()
      setToast({ message: 'Fonte rimossa', type: 'success' })
    } catch {
      setToast({ message: 'Errore rimozione fonte', type: 'error' })
    } finally {
      setConfirmDeleteFonte(null)
    }
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })
  }

  const handleDotClick = (azioneId: string) => {
    const el = azioneRefs.current[azioneId]
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      // Flash highlight
      el.classList.add('ring-2', 'ring-primary-400')
      setTimeout(() => el.classList.remove('ring-2', 'ring-primary-400'), 2000)
    }
  }

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // Filtra azioni per ricerca
  const filteredAzioni = (fascicolo?.azioni || []).filter(a => {
    if (!searchFilter) return true
    const s = searchFilter.toLowerCase()
    return a.titolo.toLowerCase().includes(s) || (a.descrizione || '').toLowerCase().includes(s)
  })

  // Tutte le azioni sono espandibili (per aggiungere schede, fonti, ecc.)
  const hasExpandableContent = (_a: AzioneFascicolo) => {
    return true
  }

  if (loading) return <div className="p-8 text-center text-gray-500">Caricamento...</div>
  if (!fascicolo) return <div className="p-8 text-center text-red-500">Fascicolo non trovato</div>

  const TipoIcon = tipoIcon[fascicolo.tipo] || FileText
  const badge = statoBadge[fascicolo.stato]

  return (
    <div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <button onClick={() => navigate('/app/fascicoli')} className="mt-1 p-1 hover:bg-gray-100 rounded">
              <ArrowLeft className="w-5 h-5 text-gray-500" />
            </button>
            <div>
              <div className="flex items-center gap-3 mb-1">
                <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">{fascicolo.numero_fascicolo}</span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                  {badge.label}
                </span>
              </div>
              {editMode ? (
                <input
                  value={editForm.titolo}
                  onChange={(e) => setEditForm({ ...editForm, titolo: e.target.value })}
                  className="text-xl font-bold text-gray-900 border-b-2 border-primary-500 outline-none w-full"
                />
              ) : (
                <h1 className="text-xl font-bold text-gray-900">{fascicolo.titolo}</h1>
              )}
              <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                <div className="flex items-center gap-1.5">
                  <TipoIcon className="w-4 h-4" />
                  <span>{fascicolo.tipo === 'testo_libero' ? fascicolo.progetto_testo : fascicolo.entity_label}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <User className="w-4 h-4" />
                  <span>{fascicolo.responsabile_nome}</span>
                </div>
              </div>
              {editMode ? (
                <div className="mt-3 space-y-2">
                  <textarea
                    value={editForm.descrizione}
                    onChange={(e) => setEditForm({ ...editForm, descrizione: e.target.value })}
                    rows={2}
                    placeholder="Descrizione..."
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                  <select
                    value={editForm.stato}
                    onChange={(e) => setEditForm({ ...editForm, stato: e.target.value as StatoFascicolo })}
                    className="px-3 py-2 border rounded-lg text-sm"
                  >
                    <option value="aperto">Aperto</option>
                    <option value="chiuso">Chiuso</option>
                    <option value="archiviato">Archiviato</option>
                  </select>
                </div>
              ) : (
                fascicolo.descrizione && (
                  <p className="text-sm text-gray-600 mt-2">{fascicolo.descrizione}</p>
                )
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {editMode ? (
              <>
                <button onClick={handleSaveEdit} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700">
                  <Save className="w-4 h-4" /> Salva
                </button>
                <button onClick={() => setEditMode(false)} className="flex items-center gap-1.5 px-3 py-1.5 border text-gray-600 rounded-lg text-sm hover:bg-gray-50">
                  <X className="w-4 h-4" /> Annulla
                </button>
              </>
            ) : (
              <>
                <button onClick={() => setEditMode(true)} className="flex items-center gap-1.5 px-3 py-1.5 border text-gray-600 rounded-lg text-sm hover:bg-gray-50">
                  <Edit3 className="w-4 h-4" /> Modifica
                </button>
                <button onClick={() => setShowDeleteFascicolo(true)} className="flex items-center gap-1.5 px-3 py-1.5 border border-red-200 text-red-600 rounded-lg text-sm hover:bg-red-50">
                  <Trash2 className="w-4 h-4" /> Elimina
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Statistiche offerte cliente */}
      {fascicolo.tipo === 'cliente' && fascicolo.entity_id && (
        <div className="mb-6">
          <ClienteStatsCard clienteId={fascicolo.entity_id} />
        </div>
      )}

      {/* Link Sorvegliati */}
      <div className="mb-4">
        <LinkSorvegliatiPanel
          fascicoloId={fascicolo.id}
          onToast={(msg, type) => setToast({ message: msg, type })}
        />
      </div>

      {/* Timeline Azioni */}
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">
          Timeline Azioni <span className="text-sm font-normal text-gray-400">({fascicolo.azioni?.length || 0})</span>
        </h2>
        <button
          onClick={() => setShowNewAzione(!showNewAzione)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
        >
          <Plus className="w-4 h-4" />
          Nuova Azione
        </button>
      </div>

      {/* Timeline orizzontale con pallini */}
      {fascicolo.azioni && fascicolo.azioni.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm mb-4 overflow-visible">
          <HorizontalTimeline
            azioni={fascicolo.azioni}
            searchFilter={searchFilter}
            onDotClick={handleDotClick}
          />
        </div>
      )}

      {/* Barra ricerca azioni */}
      {fascicolo.azioni && fascicolo.azioni.length > 2 && (
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Cerca nelle azioni..."
            value={searchFilter}
            onChange={e => setSearchFilter(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
          />
          {searchFilter && (
            <button onClick={() => setSearchFilter('')} className="absolute right-3 top-1/2 -translate-y-1/2">
              <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
            </button>
          )}
        </div>
      )}

      {/* Form Nuova Azione */}
      {showNewAzione && (
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border-2 border-primary-200">
          <h3 className="font-medium text-gray-900 mb-4">Nuova Azione</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data *</label>
              <input type="date" value={azioneForm.data_azione}
                onChange={(e) => setAzioneForm({ ...azioneForm, data_azione: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Responsabile *</label>
              <select value={azioneForm.responsabile_id}
                onChange={(e) => setAzioneForm({ ...azioneForm, responsabile_id: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg text-sm">
                <option value="">Seleziona...</option>
                {personale.map(p => <option key={p.id} value={p.id}>{p.cognome} {p.nome}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Titolo *</label>
              <input type="text" value={azioneForm.titolo}
                onChange={(e) => setAzioneForm({ ...azioneForm, titolo: e.target.value })}
                placeholder="Descrivi l'azione..."
                className="w-full px-3 py-2 border rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
              <textarea value={azioneForm.descrizione}
                onChange={(e) => setAzioneForm({ ...azioneForm, descrizione: e.target.value })}
                rows={2} className="w-full px-3 py-2 border rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Esito</label>
              <textarea value={azioneForm.esito}
                onChange={(e) => setAzioneForm({ ...azioneForm, esito: e.target.value })}
                rows={2} className="w-full px-3 py-2 border rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Stato</label>
              <select value={azioneForm.stato}
                onChange={(e) => setAzioneForm({ ...azioneForm, stato: e.target.value as StatoAzione })}
                className="w-full px-3 py-2 border rounded-lg text-sm">
                <option value="pianificata">Pianificata</option>
                <option value="completata">Completata</option>
              </select>
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                <input type="checkbox" checked={azioneForm.is_scadenza}
                  onChange={(e) => setAzioneForm({ ...azioneForm, is_scadenza: e.target.checked })}
                  className="rounded" />
                Fissa scadenza
              </label>
              {azioneForm.is_scadenza && (
                <div className="flex gap-2 mt-1">
                  <input type="date" value={azioneForm.data_scadenza}
                    onChange={(e) => setAzioneForm({ ...azioneForm, data_scadenza: e.target.value })}
                    className="flex-1 px-3 py-2 border rounded-lg text-sm" />
                  <input type="number" value={azioneForm.giorni_preavviso} min={1} max={90}
                    onChange={(e) => setAzioneForm({ ...azioneForm, giorni_preavviso: parseInt(e.target.value) || 7 })}
                    className="w-20 px-3 py-2 border rounded-lg text-sm" title="Giorni preavviso" />
                </div>
              )}
            </div>
            {/* Metti in calendario — solo per fascicoli di tipo cliente */}
            {fascicolo.tipo === 'cliente' && (
              <div className="md:col-span-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                  <input type="checkbox" checked={azioneForm.crea_evento_calendario}
                    onChange={(e) => setAzioneForm({ ...azioneForm, crea_evento_calendario: e.target.checked, data_evento: e.target.checked ? azioneForm.data_azione : '' })}
                    className="rounded" />
                  <Calendar className="w-4 h-4 text-violet-500" />
                  Metti in calendario
                </label>
                {azioneForm.crea_evento_calendario && (
                  <div className="mt-2 ml-6 flex items-center gap-3">
                    <label className="text-xs text-gray-500">Data evento:</label>
                    <input type="date" value={azioneForm.data_evento || azioneForm.data_azione}
                      onChange={(e) => setAzioneForm({ ...azioneForm, data_evento: e.target.value })}
                      className="px-3 py-1.5 border rounded-lg text-sm" />
                    <span className="text-xs text-violet-600">Verra' creata una visita commerciale nel calendario</span>
                  </div>
                )}
              </div>
            )}
            <div className="md:col-span-2 flex items-center gap-2 p-3 bg-indigo-50 rounded-lg text-sm text-indigo-700">
              <Paperclip className="w-4 h-4 flex-shrink-0" />
              Dopo la creazione, usa i pulsanti nell'azione per aggiungere schede dinamiche e fonti esterne.
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <button onClick={() => setShowNewAzione(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm">
              Annulla
            </button>
            <button onClick={handleCreateAzione} disabled={creatingAzione}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm">
              {creatingAzione ? 'Creazione...' : 'Crea Azione'}
            </button>
          </div>
        </div>
      )}

      {/* Lista azioni (card compatte) */}
      <div className="space-y-3">
        {filteredAzioni.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-500">
            <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            {searchFilter
              ? 'Nessuna azione trovata con questa ricerca'
              : 'Nessuna azione registrata. Crea la prima azione per iniziare la timeline.'}
          </div>
        ) : (
          filteredAzioni.map((azione) => {
            const azBadge = statoAzioneBadge[azione.stato]
            const isEditing = editingAzioneId === azione.id
            const scadenzaDate = azione.data_scadenza ? new Date(azione.data_scadenza) : null
            const isScaduta = scadenzaDate && scadenzaDate < new Date() && azione.stato === 'pianificata'
            const isUrgente = scadenzaDate && !isScaduta && scadenzaDate.getTime() - Date.now() < 7 * 24 * 60 * 60 * 1000 && azione.stato === 'pianificata'
            const isAuto = azione.origine === 'automatica'
            const hasContent = hasExpandableContent(azione)
            const isExpanded = expandedIds.has(azione.id) || isEditing
            const shouldShow = isExpanded

            return (
              <div
                key={azione.id}
                ref={el => { azioneRefs.current[azione.id] = el }}
                className="bg-white rounded-xl shadow-sm overflow-hidden transition-all"
              >
                {/* Header compatto azione */}
                <div
                  className={`flex items-center gap-3 px-4 py-3 ${hasContent && !isEditing ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                  onClick={() => { if (hasContent && !isEditing) toggleExpand(azione.id) }}
                >
                  {/* Marker auto/manuale */}
                  <div className={`flex-shrink-0 w-3 h-3 ${isAuto ? 'rotate-45' : 'rounded-full'}`}
                    style={{ backgroundColor: azBadge.text === 'text-green-700' ? '#22c55e' : azBadge.text === 'text-gray-500' ? '#9ca3af' : '#3b82f6' }}
                    title={isAuto ? 'Azione automatica' : 'Azione manuale'}
                  />

                  <span className="text-xs text-gray-400 w-24 flex-shrink-0">{formatDate(azione.data_azione)}</span>

                  <h3 className="font-medium text-gray-900 text-sm flex-1 min-w-0 truncate">{azione.titolo}</h3>

                  {isAuto && (
                    <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-[10px] rounded font-medium flex-shrink-0 flex items-center gap-0.5">
                      <Zap className="w-2.5 h-2.5" /> AUTO
                    </span>
                  )}

                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${azBadge.bg} ${azBadge.text} flex-shrink-0`}>
                    {azBadge.label}
                  </span>

                  {azione.is_scadenza && scadenzaDate && (
                    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 ${
                      isScaduta ? 'bg-red-100 text-red-700' : isUrgente ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      <AlertCircle className="w-2.5 h-2.5" />
                      {formatDate(azione.data_scadenza)}
                    </span>
                  )}

                  {azione.scheda_istanza_id && (
                    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 ${
                      azione.scheda_completata ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      <ClipboardList className="w-2.5 h-2.5" />
                      {azione.scheda_completata ? 'Scheda OK' : 'Scheda da compilare'}
                    </span>
                  )}

                  {azione.evento_gruppo_id && (
                    <span
                      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 bg-violet-100 text-violet-700 cursor-pointer hover:bg-violet-200"
                      title="Presente nel calendario"
                      onClick={(e) => { e.stopPropagation(); navigate(`/app/calendario?data=${azione.data_azione}`) }}
                    >
                      <Calendar className="w-2.5 h-2.5" />
                      Calendario
                    </span>
                  )}

                  {azione.links && azione.links.length > 0 && (
                    <span className="text-[10px] text-gray-400 flex-shrink-0">{azione.links.length} fonti</span>
                  )}

                  {/* Quick actions nell'header */}
                  {!isEditing && !isAuto && (
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      <button onClick={(e) => { e.stopPropagation(); startEditAzione(azione) }}
                        className="p-1 text-gray-400 hover:text-primary-600 hover:bg-gray-100 rounded" title="Modifica">
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); setDeleteAzioneId(azione.id) }}
                        className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="Elimina">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}

                  {hasContent && !isEditing && (
                    <button className="p-0.5 text-gray-400 flex-shrink-0">
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  )}
                </div>

                {/* Contenuto espanso */}
                {shouldShow && (
                  <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                    {isEditing ? (
                      /* Form edit inline */
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <input type="date" value={editAzioneForm.data_azione}
                            onChange={(e) => setEditAzioneForm({ ...editAzioneForm, data_azione: e.target.value })}
                            className="px-3 py-2 border rounded-lg text-sm" />
                          <select value={editAzioneForm.responsabile_id}
                            onChange={(e) => setEditAzioneForm({ ...editAzioneForm, responsabile_id: e.target.value })}
                            className="px-3 py-2 border rounded-lg text-sm">
                            {personale.map(p => <option key={p.id} value={p.id}>{p.cognome} {p.nome}</option>)}
                          </select>
                        </div>
                        <input type="text" value={editAzioneForm.titolo}
                          onChange={(e) => setEditAzioneForm({ ...editAzioneForm, titolo: e.target.value })}
                          className="w-full px-3 py-2 border rounded-lg text-sm" />
                        <textarea value={editAzioneForm.descrizione}
                          onChange={(e) => setEditAzioneForm({ ...editAzioneForm, descrizione: e.target.value })}
                          rows={2} placeholder="Descrizione..." className="w-full px-3 py-2 border rounded-lg text-sm" />
                        <textarea value={editAzioneForm.esito}
                          onChange={(e) => setEditAzioneForm({ ...editAzioneForm, esito: e.target.value })}
                          rows={2} placeholder="Esito..." className="w-full px-3 py-2 border rounded-lg text-sm" />
                        <div className="flex gap-3 items-center">
                          <select value={editAzioneForm.stato}
                            onChange={(e) => setEditAzioneForm({ ...editAzioneForm, stato: e.target.value })}
                            className="px-3 py-2 border rounded-lg text-sm">
                            <option value="pianificata">Pianificata</option>
                            <option value="completata">Completata</option>
                            <option value="annullata">Annullata</option>
                          </select>
                          <label className="flex items-center gap-1.5 text-sm">
                            <input type="checkbox" checked={editAzioneForm.is_scadenza}
                              onChange={(e) => setEditAzioneForm({ ...editAzioneForm, is_scadenza: e.target.checked })}
                              className="rounded" /> Scadenza
                          </label>
                          {editAzioneForm.is_scadenza && (
                            <input type="date" value={editAzioneForm.data_scadenza}
                              onChange={(e) => setEditAzioneForm({ ...editAzioneForm, data_scadenza: e.target.value })}
                              className="px-3 py-2 border rounded-lg text-sm" />
                          )}
                        </div>
                        <div className="flex justify-end gap-2">
                          <button onClick={() => { setEditingAzioneId(null); setEditAzioneForm(null) }}
                            className="px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded-lg text-sm">Annulla</button>
                          <button onClick={() => handleSaveAzione(azione.id)}
                            className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700">Salva</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {azione.descrizione && (
                          <p className="text-sm text-gray-600 mb-2">{azione.descrizione}</p>
                        )}
                        {azione.esito && (
                          <div className="mb-2 p-2 bg-green-50 rounded-lg text-sm text-green-800">
                            <span className="font-medium">Esito:</span> {azione.esito}
                          </div>
                        )}
                        <div className="flex items-center gap-2 mb-2 text-xs text-gray-500">
                          <User className="w-3 h-3" />
                          <span>{azione.responsabile_nome}</span>
                        </div>

                        {/* Schede Dinamiche — componente standard */}
                        <div onClick={(e) => e.stopPropagation()}>
                          <SchedePerEntita
                            entityType="azione_fascicolo"
                            entityId={azione.id}
                            entityLabel={azione.titolo}
                          />
                        </div>

                        {/* Bottone Aggiungi Fonti (prima dell'elenco fonti) */}
                        <div className="mt-3 mb-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); openFonteModal(azione.id) }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg border border-indigo-200"
                          >
                            <Plus className="w-3.5 h-3.5" />
                            Aggiungi Fonti
                          </button>
                        </div>

                        {/* Fonti Esterne */}
                        {azione.links && azione.links.length > 0 && (
                          <div className="space-y-2 mb-2">
                            {azione.links.map((fonte, i) => {
                              const isLink = !fonte.tipo || fonte.tipo === 'link'
                              return (
                                <div key={i} className="border rounded-lg p-3 bg-gray-50">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex items-center gap-2 min-w-0">
                                      {isLink ? (
                                        <Globe className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                      ) : (
                                        <Paperclip className="w-4 h-4 text-amber-500 flex-shrink-0" />
                                      )}
                                      <span className="text-sm font-medium text-gray-800 truncate">{fonte.titolo}</span>
                                    </div>
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                      {isLink && fonte.url && (
                                        <a href={fonte.url} target="_blank" rel="noopener noreferrer"
                                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-lg">
                                          <ExternalLink className="w-3 h-3" /> Apri
                                        </a>
                                      )}
                                      {!isLink && fonte.allegato_id && (
                                        <a href={`${api.defaults.baseURL}/v1/allegati/download/${fonte.allegato_id}`}
                                          target="_blank" rel="noopener noreferrer"
                                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 rounded-lg">
                                          <Download className="w-3 h-3" /> Scarica
                                        </a>
                                      )}
                                      <button onClick={(e) => { e.stopPropagation(); setConfirmDeleteFonte({ azioneId: azione.id, indice: i, titolo: fonte.titolo || 'questa fonte' }) }}
                                        className="p-1 text-gray-400 hover:text-red-500 rounded">
                                        <X className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  </div>
                                  {fonte.riepilogo_ai && (
                                    <div className="mt-2 p-2 bg-purple-50 rounded text-xs text-gray-700 leading-relaxed whitespace-pre-wrap">
                                      <div className="flex items-center gap-1 text-purple-600 font-medium mb-1">
                                        <Sparkles className="w-3 h-3" /> Sintesi AI
                                      </div>
                                      {fonte.riepilogo_ai}
                                    </div>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        )}

                        {/* Bottoni Modifica/Elimina */}
                        <div className="flex items-center justify-end gap-2 mt-2">
                          <button onClick={(e) => { e.stopPropagation(); startEditAzione(azione) }}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-500 hover:bg-gray-100 rounded-lg">
                            <Edit3 className="w-3.5 h-3.5" /> Modifica
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); setDeleteAzioneId(azione.id) }}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs text-red-500 hover:bg-red-50 rounded-lg">
                            <Trash2 className="w-3.5 h-3.5" /> Elimina
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>

      {/* Modale Aggiungi Fonti Esterne */}
      {fonteModalAzioneId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Aggiungi Fonte Esterna</h3>
              <button onClick={closeFonteModal} className="p-1 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Selezione tipo */}
              <div className="flex gap-2">
                <button
                  onClick={() => setFonteModalTipo('link')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 text-sm font-medium transition-colors ${
                    fonteModalTipo === 'link'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                >
                  <Globe className="w-5 h-5" />
                  Link Web
                </button>
                <button
                  onClick={() => setFonteModalTipo('allegato')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 text-sm font-medium transition-colors ${
                    fonteModalTipo === 'allegato'
                      ? 'border-amber-500 bg-amber-50 text-amber-700'
                      : 'border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                >
                  <Upload className="w-5 h-5" />
                  Allegato
                </button>
              </div>

              {/* Titolo (opzionale) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titolo (opzionale)</label>
                <input
                  type="text"
                  value={fonteModalTitolo}
                  onChange={(e) => setFonteModalTitolo(e.target.value)}
                  placeholder={fonteModalTipo === 'link' ? 'es. Articolo normativa...' : 'es. Report Q4 2025...'}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </div>

              {/* Campo specifico per tipo */}
              {fonteModalTipo === 'link' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">URL *</label>
                  <input
                    type="url"
                    value={fonteModalUrl}
                    onChange={(e) => setFonteModalUrl(e.target.value)}
                    placeholder="https://..."
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
              ) : (
                <div className="text-center p-4 border-2 border-dashed rounded-lg text-gray-500 text-sm">
                  <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  Clicca "Carica e Analizza" per selezionare il file
                </div>
              )}

              {fonteModalLoading && (
                <div className="flex items-center gap-2 text-sm text-purple-600 bg-purple-50 p-3 rounded-lg">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Analisi AI in corso...
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 p-5 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={closeFonteModal}
                disabled={fonteModalLoading}
                className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg text-sm"
              >
                Annulla
              </button>
              {fonteModalTipo === 'link' ? (
                <button
                  onClick={handleAddFonteLink}
                  disabled={fonteModalLoading || !fonteModalUrl}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
                >
                  {fonteModalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  Aggiungi e Analizza
                </button>
              ) : (
                <button
                  onClick={handleAddFonteAllegato}
                  disabled={fonteModalLoading}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 disabled:opacity-50"
                >
                  {fonteModalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  Carica e Analizza
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showDeleteFascicolo}
        title="Eliminare fascicolo"
        message="Eliminare questo fascicolo e tutte le sue azioni?"
        confirmLabel="Elimina"
        onConfirm={confirmDeleteFascicolo}
        onCancel={() => setShowDeleteFascicolo(false)}
      />

      <ConfirmModal
        isOpen={!!deleteAzioneId}
        title="Eliminare azione"
        message="Eliminare questa azione?"
        confirmLabel="Elimina"
        onConfirm={confirmDeleteAzione}
        onCancel={() => setDeleteAzioneId(null)}
      />

      {/* Modale conferma eliminazione fonte */}
      {confirmDeleteFonte && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Rimuovere fonte?</h3>
                <p className="text-sm text-gray-500 mt-0.5 truncate max-w-[220px]">{confirmDeleteFonte.titolo}</p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmDeleteFonte(null)}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
              >
                Annulla
              </button>
              <button
                onClick={() => handleRemoveFonte(confirmDeleteFonte.azioneId, confirmDeleteFonte.indice)}
                className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-lg"
              >
                Elimina
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
